// src/services/CurrencyService.js
import api from './api';

class CurrencyService {
  constructor() {
    this.rates = {
      USD: 1.0,        // Base currency
      LRD: 0.005,      // 1 LRD = 0.005 USD (example rate: 200 LRD = 1 USD)
      INR: 0.012       // 1 INR = 0.012 USD (example rate: 83 INR = 1 USD)
    };
    
    this.lastUpdated = null;
    this.isLoading = false;
    this.conversionCache = {};
    
    // Default cash game amounts in USD
    this.cashGameAmounts = {
      DEAL_RUMMY: [2, 5, 10, 50, 100],
      POINTS_RUMMY: [1, 2, 5, 10, 20] // Lower amounts for 6-player games
    };
    
    // Commission rates
    this.commissionRates = {
      CASH_GAME: 0.10,    // 10% for cash games
      FREE_PLAY: 0.00,    // No commission for free play
      CUSTOM_ROOM: 0.05   // 5% for custom rooms
    };
  }

  // ==================== PUBLIC METHODS ====================

  // Get current conversion rates
  async getRates() {
    try {
      this.isLoading = true;
      
      // Try to fetch rates from server
      const response = await api.get('/api/currency/rates');
      
      // `api` returns the parsed JSON body directly (not an axios `{ data }` wrapper).
      const rates = response?.rates || (response?.data && response.data.rates);
      if (rates) {
        this.rates = {
          USD: 1.0,
          ...rates
        };
        this.lastUpdated = new Date();
        console.log('✅ Currency rates updated:', this.rates);
      }
      
      return this.rates;
    } catch (error) {
      console.warn('⚠️ Using cached currency rates. Could not fetch from server:', error.message);
      return this.rates;
    } finally {
      this.isLoading = false;
    }
  }

  // Convert amount from one currency to another
  convert(amount, fromCurrency, toCurrency) {
    if (!amount || amount <= 0) return 0;
    
    // Check cache
    const cacheKey = `${amount}_${fromCurrency}_${toCurrency}`;
    if (this.conversionCache[cacheKey]) {
      return this.conversionCache[cacheKey];
    }
    
    // Change the API endpoint
const fetchRatesFromServer = async () => {
    try {
        const response = await axios.get(`${API_URL}/api/currency/rates`);
        
        if (response.data.success) {
            const { baseCurrency, rates, timestamp } = response.data;
            cacheRates(baseCurrency, rates, timestamp);
            return rates;
        } else {
            throw new Error('Failed to fetch rates from server');
        }
    } catch (error) {
        console.error('⚠️ Using cached currency rates. Could not fetch from server:', error.message);
        // Return cached rates if available
        const cached = localStorage.getItem('currencyRates');
        if (cached) {
            const { rates, expiry } = JSON.parse(cached);
            if (new Date(expiry) > new Date()) {
                return rates;
            }
        }
        // Fallback to default rates
        return DEFAULT_RATES;
    }
};
    // Convert via USD as base
    const fromRate = this.rates[fromCurrency] || 1;
    const toRate = this.rates[toCurrency] || 1;
    
    // Convert to USD first, then to target currency
    const amountInUSD = amount / fromRate;
    const convertedAmount = amountInUSD * toRate;
    
    // Round to 2 decimal places for currency
    const roundedAmount = Math.round(convertedAmount * 100) / 100;
    
    // Cache result
    this.conversionCache[cacheKey] = roundedAmount;
    
    return roundedAmount;
  }

  // Convert USD amount to target currency (for cash game display)
  convertFromUSD(usdAmount, targetCurrency) {
    if (!usdAmount || usdAmount <= 0) return 0;
    
    if (targetCurrency === 'USD') {
      return usdAmount;
    }
    
    const rate = this.rates[targetCurrency];
    if (!rate) {
      console.error(`❌ No conversion rate for ${targetCurrency}`);
      return usdAmount;
    }
    
    const convertedAmount = usdAmount / rate;
    return Math.round(convertedAmount * 100) / 100;
  }

  // Get cash game amounts in specific currency
  getCashGameAmounts(gameType, currency = 'USD') {
    const baseAmounts = this.cashGameAmounts[gameType] || [];
    
    if (currency === 'USD') {
      return baseAmounts;
    }
    
    return baseAmounts.map(amount => ({
      usd: amount,
      converted: this.convertFromUSD(amount, currency)
    }));
  }

  // Calculate winnings after commission
  calculateWinnings(totalPot, position, currency = 'USD', gameType = 'CASH_GAME') {
    if (!totalPot || totalPot <= 0) return 0;
    
    const commissionRate = this.commissionRates[gameType] || 0.10;
    
    // Prize distribution based on position
    const prizeDistribution = {
      1: 0.50, // 50% for 1st place
      2: 0.30, // 30% for 2nd place
      3: 0.20, // 20% for 3rd place
      4: 0.10, // 10% for 4th place
      5: 0.05, // 5% for 5th place
      6: 0.00  // 0% for 6th place
    };
    
    const prizePercentage = prizeDistribution[position] || 0;
    const grossWinnings = totalPot * prizePercentage;
    
    // Apply commission
    const commission = grossWinnings * commissionRate;
    const netWinnings = grossWinnings - commission;
    
    return {
      grossWinnings,
      commission,
      netWinnings,
      currency,
      commissionRate,
      prizePercentage
    };
  }

  // Format currency amount with symbol
  format(amount, currency = 'USD') {
    if (typeof amount !== 'number') {
      amount = parseFloat(amount) || 0;
    }
    
    const symbols = {
      USD: '$',
      LRD: 'L$',
      INR: '₹',
      GMD: 'D',
      CFA: 'CFA',
      XOF: 'CFA',
      GHS: 'GH₵',
      NGN: '₦',
      SLE: 'Le'
    };
    
    const symbol = symbols[currency] || currency;
    
    // Format with commas and 2 decimal places
    const formattedAmount = amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    return `${symbol}${formattedAmount}`;
  }

  // Get currency symbol
  getSymbol(currency = 'USD') {
    const symbols = {
      USD: '$',
      LRD: 'L$',
      INR: '₹',
      GMD: 'D',
      CFA: 'CFA',
      XOF: 'CFA',
      GHS: 'GH₵',
      NGN: '₦',
      SLE: 'Le'
    };
    
    return symbols[currency] || currency;
  }

  // Get currency name
  getName(currency = 'USD') {
    const names = {
      USD: 'US Dollar',
      LRD: 'Liberian Dollar',
      INR: 'Indian Rupee',
      GMD: 'Gambian Dalasi',
      CFA: 'West African CFA Franc',
      XOF: 'West African CFA Franc',
      GHS: 'Ghanaian Cedi',
      NGN: 'Nigerian Naira',
      SLE: 'Sierra Leonean Leone'
    };
    
    return names[currency] || currency;
  }

  // Get all available currencies
  getAvailableCurrencies() {
    return Object.keys(this.rates);
  }

  // Calculate required balance for game entry
  getRequiredBalance(gameAmountUSD, selectedCurrency) {
    const requiredAmount = this.convertFromUSD(gameAmountUSD, selectedCurrency);
    return {
      usd: gameAmountUSD,
      converted: requiredAmount,
      currency: selectedCurrency,
      formatted: this.format(requiredAmount, selectedCurrency)
    };
  }

  // Get commission breakdown
  getCommissionBreakdown(gameType = 'CASH_GAME') {
    const commissionRate = this.commissionRates[gameType] || 0;
    return {
      rate: commissionRate,
      percentage: commissionRate * 100,
      description: `${commissionRate * 100}% of winnings goes to commission`
    };
  }

  // ==================== ADMIN METHODS ====================

  // Update conversion rates (admin only)
  async updateRates(newRates) {
    try {
      const response = await api.post('/api/admin/currency/rates', {
        rates: newRates,
        updated_by: 'admin' // In real app, use actual admin ID
      });
      
      if (response?.success || response?.data?.success) {
        this.rates = {
          USD: 1.0,
          ...newRates
        };
        this.conversionCache = {}; // Clear cache
        this.lastUpdated = new Date();
        console.log('✅ Currency rates updated by admin:', this.rates);
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Failed to update currency rates:', error);
      throw error;
    }
  }

  // Add new currency (admin only)
  async addCurrency(currencyCode, rateToUSD, name) {
    try {
      const response = await api.post('/api/admin/currency/add', {
        code: currencyCode,
        rate_to_usd: rateToUSD,
        name: name
      });
      
      if (response?.success || response?.data?.success) {
        this.rates[currencyCode] = rateToUSD;
        console.log(`✅ Currency ${currencyCode} added with rate: ${rateToUSD}`);
        return true;
      }
      return false;
    } catch (error) {
      console.error(`❌ Failed to add currency ${currencyCode}:`, error);
      throw error;
    }
  }

  // Get conversion rate between two currencies
  getRate(fromCurrency, toCurrency) {
    if (fromCurrency === toCurrency) return 1;
    
    const fromRate = this.rates[fromCurrency];
    const toRate = this.rates[toCurrency];
    
    if (!fromRate || !toRate) {
      console.error(`❌ Missing conversion rates: ${fromCurrency} or ${toCurrency}`);
      return 1;
    }
    
    // Convert via USD
    return toRate / fromRate;
  }

  // Get reverse rate (for display purposes)
  getReverseRate(currency) {
    const rate = this.rates[currency];
    if (!rate) return 0;
    
    return 1 / rate;
  }

  // ==================== UTILITY METHODS ====================

  // Clear cache
  clearCache() {
    this.conversionCache = {};
  }

  // Get service status
  getStatus() {
    return {
      rates: this.rates,
      lastUpdated: this.lastUpdated,
      isLoading: this.isLoading,
      availableCurrencies: this.getAvailableCurrencies(),
      commissionRates: this.commissionRates
    };
  }

  // Validate currency code
  isValidCurrency(currency) {
    return this.rates.hasOwnProperty(currency);
  }

  // Get minimum and maximum amounts for a game type
  getAmountRange(gameType) {
    const amounts = this.cashGameAmounts[gameType] || [];
    return {
      min: Math.min(...amounts),
      max: Math.max(...amounts),
      amounts: amounts
    };
  }

  // Calculate total pot for a game
  calculateTotalPot(playerCount, entryFeeUSD, currency) {
    const totalPotUSD = playerCount * entryFeeUSD;
    
    if (currency === 'USD') {
      return {
        usd: totalPotUSD,
        converted: totalPotUSD,
        currency: 'USD'
      };
    }
    
    const convertedPot = this.convertFromUSD(totalPotUSD, currency);
    
    return {
      usd: totalPotUSD,
      converted: convertedPot,
      currency: currency
    };
  }
}

// Create singleton instance
const currencyService = new CurrencyService();

// Initialize rates on app start
currencyService.getRates().catch(() => {
  console.log('Using default currency rates');
});

export default currencyService;